import { useEffect, useRef, useState } from 'react';

const MAX_UPLOAD_ACK_RETRIES = 20;
const UPLOAD_ACK_RETRY_DELAY_MS = 500;

type UploadCompleteHandler = (message: { role: string; content: string }) => boolean | void | Promise<boolean | void>;

export function useUploadCompletionAck({
  isUploadComplete,
  messageContent,
  uploadAckRetryKey,
  onUploadComplete,
}: {
  isUploadComplete: boolean;
  messageContent: string;
  uploadAckRetryKey?: string;
  onUploadComplete?: UploadCompleteHandler;
}): void {
  const hasTriggeredMessageRef = useRef(false);
  const ackInFlightRef = useRef(false);
  const ackRetryCountRef = useRef(0);
  const ackRetryTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const [ackRetryNonce, setAckRetryNonce] = useState(0);
  const onUploadCompleteRef = useRef(onUploadComplete);
  onUploadCompleteRef.current = onUploadComplete;

  const clearAckRetryTimeout = () => {
    if (ackRetryTimeoutRef.current != null) {
      window.clearTimeout(ackRetryTimeoutRef.current);
      ackRetryTimeoutRef.current = null;
    }
  };

  const scheduleAckRetry = () => {
    if (!isMountedRef.current || ackRetryCountRef.current >= MAX_UPLOAD_ACK_RETRIES) {
      return;
    }

    clearAckRetryTimeout();
    ackRetryCountRef.current += 1;
    ackRetryTimeoutRef.current = window.setTimeout(() => {
      ackRetryTimeoutRef.current = null;
      if (isMountedRef.current) {
        setAckRetryNonce((nonce) => nonce + 1);
      }
    }, UPLOAD_ACK_RETRY_DELAY_MS);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearAckRetryTimeout();
    };
  }, []);

  useEffect(() => {
    ackRetryCountRef.current = 0;
    clearAckRetryTimeout();
  }, [uploadAckRetryKey]);

  useEffect(() => {
    if (!isUploadComplete || !onUploadCompleteRef.current || hasTriggeredMessageRef.current || ackInFlightRef.current) {
      return;
    }

    ackInFlightRef.current = true;

    void (async () => {
      try {
        const sent = await Promise.resolve(
          onUploadCompleteRef.current!({
            role: 'user',
            content: messageContent,
          })
        );

        if (sent !== false) {
          hasTriggeredMessageRef.current = true;
        } else {
          scheduleAckRetry();
        }
      } catch {
        scheduleAckRetry();
      } finally {
        ackInFlightRef.current = false;
      }
    })();
  }, [isUploadComplete, messageContent, uploadAckRetryKey, ackRetryNonce]);
}
